import { useState } from 'react';
import { useNotifications } from '../hooks/use-notifications';
import { useUserLocation } from '@/Locations/hooks/use-user-location';
import type { Notification } from '../types/notifications';
import { IconX } from '@tabler/icons-react';

export const NotificationBanner = () => {
  const { data: notifications } = useNotifications();
  const { selectedLocation } = useUserLocation();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  if (!notifications || notifications.length === 0) return null;

  // Filter notifications by location:
  // - Show if notification has no locations (global notification)
  // - Show if user's selected location matches one of the notification's locations
  const isNotificationVisible = (notification: Notification): boolean => {
    // No locations = global notification, show to everyone
    if (!notification.locations || notification.locations.length === 0) {
      return true;
    }
    // If user has no location selected, only show global notifications
    if (!selectedLocation) {
      return false;
    }
    // Check if user's location matches any of the notification's locations
    return notification.locations.some(
      (loc) => loc.LocationTitle === selectedLocation
    );
  };

  // Show the first non-dismissed, location-filtered notification
  const activeNotification = notifications.find(
    (n) => !dismissedIds.has(n.NotificationTitle) && isNotificationVisible(n)
  );

  if (!activeNotification) return null;

  const handleDismiss = () => {
    setDismissedIds((prev) => new Set([...prev, activeNotification.NotificationTitle]));
  };

  return (
    <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-orange-500 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
      {/* Decorative gradient blurs */}
      <div
        aria-hidden="true"
        className="absolute left-[max(-7rem,calc(50%-52rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
      >
        <div
          style={{
            clipPath:
              'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
          }}
          className="aspect-[577/310] w-[36rem] bg-gradient-to-r from-orange-400/40 to-red-400/40 opacity-50"
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute left-[max(45rem,calc(50%+8rem))] top-1/2 -z-10 -translate-y-1/2 transform-gpu blur-2xl"
      >
        <div
          style={{
            clipPath:
              'polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)',
          }}
          className="aspect-[577/310] w-[36rem] bg-gradient-to-r from-orange-400/40 to-red-400/40 opacity-50"
        />
      </div>

      {/* Content */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-sm leading-6 text-white">
          <strong className="font-semibold">{activeNotification.NotificationTitle}</strong>
          {activeNotification.NotificationDetails && (
            <>
              <svg
                viewBox="0 0 2 2"
                aria-hidden="true"
                className="mx-2 inline h-0.5 w-0.5 fill-current"
              >
                <circle r="1" cx="1" cy="1" />
              </svg>
              {activeNotification.NotificationDetails}
            </>
          )}
        </p>
        {activeNotification.NotificationHasURL && activeNotification.NotificationURL && (
          <a
            href={activeNotification.NotificationURL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-none rounded-full bg-white/10 px-3.5 py-1 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-white/20 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            {activeNotification.NotificationButtonText || 'Learn more'} <span aria-hidden="true">&rarr;</span>
          </a>
        )}
      </div>

      {/* Dismiss button */}
      <div className="flex flex-1 justify-end">
        <button
          type="button"
          onClick={handleDismiss}
          className="-m-3 p-3 focus-visible:outline-offset-[-4px]"
        >
          <span className="sr-only">Dismiss</span>
          <IconX className="h-5 w-5 text-white" />
        </button>
      </div>
    </div>
  );
};
